import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { v4 as uuidv4 } from 'uuid';

import keycloak from '@/keycloak';

interface FilteredError {
  status: number;
  message: string;
  method: string;
  cause?: string;
}

interface AuthOptionalRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  _retry?: boolean;
}

const PUBLIC_ROUTES = ['/user/activate', '/new-password', '/reset-password'];

const http = axios.create();

const shouldSkipAuth = (config?: { skipAuth?: boolean }): boolean => config?.skipAuth === true;

const isPublicRoute = (): boolean =>
  PUBLIC_ROUTES.some(route => {
    const { pathname } = window.location;

    return pathname === route || pathname.startsWith(`${route}/`);
  });

const getAuthToken = (): string | undefined => keycloak.token;

let refreshTokenPromise: Promise<boolean> | undefined;

const updateTokenSafely = async (minValidity: number): Promise<boolean> => {
  refreshTokenPromise ??= keycloak.updateToken(minValidity).finally(() => {
    refreshTokenPromise = undefined;
  });

  return refreshTokenPromise;
};

const removeAuthorizationHeaders = (config: InternalAxiosRequestConfig): void => {
  const headers = AxiosHeaders.from(config.headers);

  headers.delete('Authorization');
  config.headers = headers;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]';

const hasTransactionId = (data: unknown): boolean => {
  if (data instanceof FormData) {
    return data.has('transactionId');
  }

  if (data instanceof URLSearchParams) {
    return data.has('transactionId');
  }

  return isPlainObject(data) && typeof data.transactionId !== 'undefined';
};

const addTransactionId = (config: InternalAxiosRequestConfig): void => {
  const transactionId = uuidv4();
  const data: unknown = config.data;

  if (hasTransactionId(data)) {
    return;
  }

  if (data instanceof FormData) {
    data.append('transactionId', transactionId);
    return;
  }

  if (data instanceof URLSearchParams) {
    data.append('transactionId', transactionId);
    return;
  }

  if (isPlainObject(data)) {
    config.data = {
      ...data,
      transactionId,
    };
    return;
  }

  if (data === null || data === undefined) {
    config.data = { transactionId };
  }
};

export const refreshAuthToken = async (): Promise<void> => {
  if (isPublicRoute()) {
    return;
  }

  try {
    await updateTokenSafely(30);
  } catch (error) {
    console.error('Token refresh failed', error);
  }
};

export const createAuthorizationHeaders = async (): Promise<HeadersInit | undefined> => {
  await refreshAuthToken();

  const token = getAuthToken();

  if (!token) {
    return undefined;
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};

const applyAuthorizationHeader = async (config: InternalAxiosRequestConfig): Promise<void> => {
  const requestConfig = config as AuthOptionalRequestConfig;

  if (shouldSkipAuth(requestConfig)) {
    removeAuthorizationHeaders(requestConfig);

    return;
  }

  await refreshAuthToken();

  const token = getAuthToken();

  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
};

const formatAxiosError = (error: AxiosError): Error => {
  if (error.response) {
    const { status, data, statusText } = error.response;
    const method = (error.config?.method ?? 'unknown').toUpperCase();

    const responseData = (data ?? {}) as Record<string, unknown>;
    const hasData = Object.keys(responseData).length > 0;

    const filteredError: FilteredError = {
      status: status || 500,
      message:
        (responseData.message as string) ||
        (hasData ? JSON.stringify(responseData) : statusText) ||
        'Unknown error occurred',
      method,
      ...(typeof responseData.cause === 'string' ? { cause: responseData.cause } : {}),
    };

    return new Error(JSON.stringify(filteredError));
  }

  const networkError: FilteredError = {
    status: 0,
    message: error.message || 'Network error occurred',
    method: 'UNKNOWN',
  };

  return new Error(JSON.stringify(networkError));
};

http.interceptors.request.use(
  async config => {
    await applyAuthorizationHeader(config);
    const method = config.method?.toLowerCase() ?? 'get';
    if (method !== 'get') {
      addTransactionId(config);
    }

    return config;
  },
  (error: { message: string }) => Promise.reject(new Error(error.message))
);

http.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const requestConfig = error.config as AuthOptionalRequestConfig | undefined;

    if (
      error.response?.status === 401 &&
      requestConfig &&
      !requestConfig._retry &&
      !shouldSkipAuth(requestConfig) &&
      !isPublicRoute()
    ) {
      try {
        requestConfig._retry = true;
        const isValid = await updateTokenSafely(5);
        const token = getAuthToken();

        if (isValid && token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }

        return await http.request(requestConfig);
      } catch {
        keycloak.login();
        return Promise.reject(formatAxiosError(error));
      }
    }

    return Promise.reject(formatAxiosError(error));
  }
);

export default http;
